import { OverlayLoader } from 'components/Loaders'
import { actions, selectors } from 'data'
import { PaymentState } from 'data/Payments/Payment/types'
import React, { useEffect } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import GeneralError from 'scenes/Cashflow/LinkBank/GeneralError'
import ConnectBank from 'scenes/Cashflow/LinkBank/Yapily/ConnectBank'
import DataError from 'scenes/DataError'

import DepositPending from './DepositPending'
import WaitingFromBank from './WaitingFromBank'

type Props = {
  // eslint-disable-next-line react/no-unused-prop-types
  id: string // need this even not used in component so react-redux will export it
  onClose: () => void
} & ConnectedProps<typeof connector>

const FETCH_INTERVAL = 1000 * 20

const DONE_STATUS = [
  PaymentState.CREDITED,
  PaymentState.FRAUD_REVIEW,
  PaymentState.MANUAL_REVIEW,
  PaymentState.CLEARED,
  PaymentState.COMPLETE
]

const PaymentStatus = ({ bank, deposit, error, fetch, fetchBank, onClose }: Props) => {
  useEffect(function transactionPollEffect() {
    fetch()
    const interval = setInterval(fetch, FETCH_INTERVAL)
    return () => clearInterval(interval)
  }, [])

  useEffect(
    function fetchBankEffect() {
      if (deposit?.beneficiaryId) fetchBank(deposit?.beneficiaryId)
    },
    [deposit?.beneficiaryId]
  )

  if (error) {
    return <DataError error={error} />
  }

  if (!deposit || !bank) return <OverlayLoader />

  if (deposit.state === PaymentState.FAILED || deposit.state === PaymentState.REJECTED) {
    return (
      <GeneralError error={deposit?.extraAttributes?.error ?? deposit?.error} onBack={onClose} />
    )
  }

  if (DONE_STATUS.indexOf(deposit.state) > -1) {
    return <DepositPending deposit={deposit} onClose={onClose} />
  }

  // We only handle pending yapily payments for now
  if (
    bank.partner !== 'YAPILY' ||
    !deposit.extraAttributes ||
    !deposit.extraAttributes.authorisationUrl
  ) {
    return <WaitingFromBank bankName={bank.details?.bankName ?? 'Bank'} />
  }

  return (
    <ConnectBank
      institutionImg={bank?.attributes?.media[0].source ?? ''}
      linkUrl={deposit.extraAttributes.authorisationUrl}
      onBack={onClose}
      qrImg={deposit.extraAttributes.qrcodeUrl}
      title='approve_your_deposit'
    />
  )
}

const mapStateToProps = (state, { id }) => {
  const deposit = selectors.payments.payment.getPayment(state, id)

  return {
    bank: deposit
      ? selectors.payments.bankTransfer.getBank(state)(deposit.beneficiaryId)
      : undefined,
    deposit,
    error: selectors.payments.payment.getPaymentError(state, id)
  }
}

const mapDispatchToProps = (dispatch, { id }) => ({
  fetch: () => {
    dispatch(actions.payments.payment.fetch(id))
  },
  fetchBank: (id) => {
    dispatch(actions.payments.fetchBank(id))
  }
})

const connector = connect(mapStateToProps, mapDispatchToProps)

export default connector(PaymentStatus)
